import { SearchOutlined } from '@ant-design/icons';
import { Input, Skeleton, Typography } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { metaForFieldType } from '../fieldMeta';

import type { CatalogFieldDefinition } from './catalogFields';

export type { CatalogFieldDefinition };

const { Text } = Typography;

const CARD_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff',
  textAlign: 'left',
  width: '100%',
  cursor: 'pointer',
  transition: 'border-color 140ms ease, background 140ms ease, box-shadow 140ms ease',
};

const ICON_TILE: CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 7,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 15,
  flexShrink: 0,
};

/**
 * Card text is plain spans with CSS ellipsis rather than antd `Typography.Text`.
 *
 * Typography is built for prose, and it pays for that per instance: antd wraps every `Text`
 * in an rc-resize-observer, and a `Text` with `ellipsis` additionally constructs its own
 * IntersectionObserver to spot native truncation (antd/es/typography/Base — the
 * ResizeObserver wrapper and the IntersectionObserver effect). Two of those per card put
 * ~120 live observers on the first page alone and grow with every page the infinite scroll
 * appends, which is mounting cost that memoising the card cannot touch. Nothing here needs
 * measuring — a fixed-width rail truncating a single line is exactly what
 * `text-overflow: ellipsis` does for free.
 */
const CARD_BODY: CSSProperties = {
  flex: 1,
  // Without this the flex child refuses to shrink below its content and never truncates.
  minWidth: 0,
};

/**
 * antd's `token.lineHeight`, which `.ant-typography` applies to every `Text` — and so to
 * the Palette tab's cards. Derived the way antd derives it, `(fontSize + 8) / fontSize`
 * against the theme's 14px base token, rather than pasted as a magic 1.571.
 */
const ANTD_BASE_FONT_SIZE = 14;
const ANTD_LINE_HEIGHT = (ANTD_BASE_FONT_SIZE + 8) / ANTD_BASE_FONT_SIZE;

/**
 * Matched to the Palette tab's card text (FieldPalette.tsx `PaletteCard`) so the two tabs
 * read as one list. That card uses `<Text strong>`, which antd renders as a real `<strong>`
 * inside `.ant-typography` — nothing in the theme or global.css overrides `strong`, so it
 * lands on the UA default of 700, NOT the 600 these spans would otherwise look right at.
 */
const CARD_TITLE: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  lineHeight: ANTD_LINE_HEIGHT,
  color: '#111827',
  // The one intentional departure from PaletteCard: harvested catalog labels run long
  // ("Subscriber's Canadian province or Canadian territory of residence") where the
  // palette's are hand-written and short, so these truncate instead of wrapping the card.
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** PaletteCard's description line, which overrides antd's line-height with 1.35. */
const CARD_SUBTITLE: CSSProperties = {
  display: 'block',
  fontSize: 11,
  lineHeight: 1.35,
  color: '#475569',
};

/**
 * Cards added per page of the infinite scroll. The catalog runs to hundreds of entries and
 * the rail is a 296px column, so mounting all of them puts most of the work into DOM the
 * originator has not scrolled to yet. Sized to overfill a tall rail, so the sentinel below
 * starts out off-screen and the first page never triggers an immediate second one.
 */
const PAGE_SIZE = 60;

/**
 * How far below the fold the sentinel starts pulling the next page, so cards are mounted
 * before they are scrolled into view rather than after.
 */
const PREFETCH_MARGIN = '200px';

/**
 * Characters required before the search filters anything. A single letter matches most of
 * the catalog, so it does the work of a full scan to produce a list no shorter than the
 * one already on screen — and it lands mid-word on the way to a real query. Below this the
 * list is left alone and the hint says why, rather than the box appearing to do nothing.
 */
const MIN_SEARCH_LENGTH = 2;

/**
 * Everything a card draws that depends on the field's TYPE rather than the field itself.
 * Built once per type and shared by every card of that type, so the icon element and the
 * translated type name are created ~16 times instead of once per visible card — and the
 * shared identity is what lets the memoised card skip re-rendering.
 */
type CatalogCardVisual = {
  typeLabel: string;
  tintBg: string;
  tintFg: string;
  icon: ReactNode;
};

/** A catalog entry with its per-type visuals and search text resolved up front. */
type CatalogRow = {
  entry: CatalogFieldDefinition;
  visual: CatalogCardVisual;
  /** Both labels pre-lowercased — otherwise each keystroke re-cases ~700 strings. */
  haystack: string;
};

/**
 * "Catalog" tab body for the field palette — a predefined/curated list of
 * fields, distinct from the "Custom" tab's user-saved elements. Cards behave
 * exactly like the Palette tab's own field cards: click to arm, click an empty
 * canvas column to place.
 *
 * The field data itself is NOT statically imported: it is ~280 KB of object literals, and
 * a static import puts it in the main bundle where every advisor downloads and parses it
 * to render a page that has no builder on it. The dynamic import below gives it its own
 * chunk, fetched the first time this tab is mounted. See customElements/catalogFields.ts.
 */
export function CatalogTabContent({
  armedFieldId,
  onPickField,
}: {
  armedFieldId?: string | null;
  onPickField: (entry: CatalogFieldDefinition) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [fields, setFields] = useState<CatalogFieldDefinition[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('./catalogFields')
      .then((mod) => {
        if (!cancelled) setFields(mod.CATALOG_FIELDS);
      })
      .catch(() => {
        // Almost always a stale chunk hash after a deploy. Say so rather than leaving the
        // skeleton spinning forever — a reload fetches the current manifest.
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Filtering the whole catalog on every keystroke is enough work to drop frames on a
  // modest machine. Deferring it lets the input paint the typed character immediately and
  // the list catch up a tick later, rather than the two contending for the same frame.
  const deferredSearch = useDeferredValue(search);

  // Resolve every entry's type visuals and search text ONCE, when the chunk lands — not per
  // card and not per keystroke. `metaForFieldType` and `t()` used to run inside the card, so
  // scrolling re-did them for each of hundreds of cards. Rebuilt only if the language
  // changes, which is what `t`'s identity tracks.
  const rows = useMemo<CatalogRow[]>(() => {
    if (fields === null) return [];
    const byType = new Map<CatalogFieldDefinition['type'], CatalogCardVisual>();
    return fields.map((entry) => {
      let visual = byType.get(entry.type);
      if (visual === undefined) {
        const meta = metaForFieldType(entry.type);
        visual = {
          typeLabel: t(meta.labelKey),
          tintBg: meta.tintBg,
          tintFg: meta.tintFg,
          icon: meta.icon(),
        };
        byType.set(entry.type, visual);
      }
      return { entry, visual, haystack: `${entry.labelEn}\n${entry.labelFr}`.toLowerCase() };
    });
  }, [fields, t]);

  const needle = deferredSearch.trim().toLowerCase();
  const searchTooShort = needle.length > 0 && needle.length < MIN_SEARCH_LENGTH;

  const matches = useMemo(() => {
    if (needle.length < MIN_SEARCH_LENGTH) return rows;
    return rows.filter((row) => row.haystack.includes(needle));
  }, [rows, needle]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const shown = visibleCount >= matches.length ? matches : matches.slice(0, visibleCount);
  const hasMore = matches.length > shown.length;

  // A new list starts back at the first page, or a search narrowed after scrolling deep
  // would render its handful of matches against a count meant for the whole catalog.
  // Keyed on `matches` itself — memoised, so its identity changes exactly when the list
  // does and not on the keystrokes below MIN_SEARCH_LENGTH that filter nothing.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [matches]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!hasMore || node === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((count) => count + PAGE_SIZE);
        }
      },
      { rootMargin: PREFETCH_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // Rebuilt on every page: IntersectionObserver reports intersection CHANGES, so an
    // observer whose sentinel is still visible after a page lands stays silent and the
    // list stalls short of the fold. A fresh observer always delivers an initial record,
    // which keeps pulling pages until the sentinel is pushed out of view or `hasMore`
    // goes false. The callbacks are async, so this fills progressively rather than
    // spinning the main thread.
  }, [hasMore, visibleCount]);

  // `onPickField` arrives as a fresh arrow on every FieldPalette render, so handing it
  // straight to a memo()'d card would change a prop every time and defeat the memo. That
  // memo is what makes the infinite scroll cheap — appending a page must not re-render the
  // pages already on screen. The ref holds the current callback; `handlePick` keeps one
  // identity for the tab's life.
  const onPickFieldRef = useRef(onPickField);
  useEffect(() => {
    onPickFieldRef.current = onPickField;
  });
  const handlePick = useCallback((entry: CatalogFieldDefinition) => {
    onPickFieldRef.current(entry);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Input
        placeholder={t('workflow.customElements.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        size="small"
        suffix={<SearchOutlined style={{ color: '#6b7280' }} />}
      />

      {loadFailed && (
        <Text type="danger" style={{ fontSize: 12, padding: '0 4px' }}>
          {t('workflow.builder.palette.catalogLoadFailed')}
        </Text>
      )}

      {fields === null && !loadFailed && (
        <Skeleton active title={false} paragraph={{ rows: 6 }} style={{ padding: '0 4px' }} />
      )}

      {searchTooShort && (
        <Text type="secondary" style={{ fontSize: 12, padding: '0 4px' }}>
          {t('workflow.builder.palette.catalogSearchMin', { min: MIN_SEARCH_LENGTH })}
        </Text>
      )}

      {fields !== null && matches.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12, padding: '0 4px' }}>
          {t('workflow.customElements.empty')}
        </Text>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shown.map((row) => (
          <CatalogFieldCard
            key={row.entry.id}
            row={row}
            armed={armedFieldId === row.entry.id}
            onPick={handlePick}
          />
        ))}
      </div>

      {hasMore && (
        <div ref={sentinelRef} style={{ padding: '0 4px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workflow.builder.palette.catalogLoadingMore', {
              shown: shown.length,
              total: matches.length,
            })}
          </Text>
        </div>
      )}
    </div>
  );
}

/**
 * One catalog card. Deliberately free of hooks that scale with the list: no
 * `useTranslation` (the type label arrives pre-translated on `row.visual`, so a page of
 * cards adds one i18n subscriber instead of sixty) and no antd `Typography` — see the
 * CARD_TITLE comment. With `onPick` stable from the parent, every prop here is
 * reference-stable per entry, which is what makes the memo actually hold.
 */
const CatalogFieldCard = memo(function CatalogFieldCard({
  row,
  armed,
  onPick,
}: {
  row: CatalogRow;
  armed: boolean;
  onPick: (entry: CatalogFieldDefinition) => void;
}) {
  const { entry, visual } = row;
  return (
    <button
      type="button"
      onClick={() => onPick(entry)}
      style={{
        ...CARD_BASE,
        background: armed ? visual.tintBg : '#fff',
        borderColor: armed ? visual.tintFg : '#e5e7eb',
        boxShadow: armed
          ? `0 2px 6px ${visual.tintBg}, 0 0 0 1px ${visual.tintFg}`
          : '0 1px 2px rgba(0, 0, 0, 0.03)',
      }}
    >
      <span style={{ ...ICON_TILE, background: visual.tintBg, color: visual.tintFg }}>
        {visual.icon}
      </span>
      <div style={CARD_BODY}>
        {/* `title` keeps the full label reachable once CSS truncates it. */}
        <span style={CARD_TITLE} title={entry.labelEn}>
          {entry.labelEn}
        </span>
        <span style={CARD_SUBTITLE}>{visual.typeLabel}</span>
      </div>
    </button>
  );
});
